/* ==========================================================================================================
   EcoVessel 2026 · ev-home.js · the home page v2 behaviours (docs/HOME-V2.md, docs/MOTION-V2.md)
   No dependencies. Loaded deferred by the home sections (printed by several: the second copy does nothing but
   bind new markup). Binds each element once, re-binds on shopify:section:load, lets go on :unload.
   prefers-reduced-motion: no autoplay, no glide, no count-up, instant colour changes.

   [data-evh-hero]   the hero slider (sections/ev-home-hero.liquid)
                     - autoplay from the start (data-autoplay, data-dur seconds a photo); holds while the mouse is
                       over the photo, keyboard focus is inside, a finger is on it, it is off screen, the tab is hidden,
                       or a slide is selected in the theme editor; a tap on a dot or a swipe stops it for good;
                     - photos 2+ wait in data-srcset until photo 1 has arrived (the next) / the page has loaded (all);
                     - before a photo is FIRST shown it is decoded and painted once as a 1 px strip (.is-prepaint),
                       so Chrome never counts it as a new, larger LCP (Blenders Online's fix-seo 02-10 trick);
                     - autoplay never moves on to a photo that has not arrived.
   [data-evh-show]   "Pick your bottle": a colour swatch [data-evh-v] previews its colour under the pointer and
                     chooses it on click / tap / Enter: photo (cross-fade), colour name, price, was-price, saving,
                     % badge, "Only N left in <colour>", the cart form's variant id, the links. Colour of the day:
                     a returning visitor (localStorage counter) starts on the next colour.
   [data-evh-mq]     an endless glide (the moving strip, the reviews): the group is moved by exactly its own width
                     at data-speed px/s; copies are added when one group is narrower than the screen; it eases to
                     a stop under the pointer, with keyboard focus inside, while a finger is on it (resumes after
                     2.5 s), and pauses off screen.
   [data-evh-flip]   a sale countdown whose digits slide in as they change.
   ========================================================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.EVHome) { W.EVHome.init(D); return; }

  const mq = W.matchMedia('(prefers-reduced-motion: reduce)');
  const reduce = () => mq.matches;
  const hasIO = 'IntersectionObserver' in W;
  const bound = new WeakMap();
  const once = (el, key) => { let s = bound.get(el); if (!s) bound.set(el, (s = new Set())); if (s.has(key)) return false; s.add(key); return true; };
  const offs = new Set();
  const hold = (el, off) => offs.add({ el, off });
  const qsa = (root, sel) => { const l = Array.from(root.querySelectorAll(sel)); if (root !== D && root.matches && root.matches(sel)) l.unshift(root); return l; };
  const idle = (fn) => (W.requestIdleCallback ? W.requestIdleCallback(fn, { timeout: 2500 }) : setTimeout(fn, 800));

  /* ------------------------------------------------------------------------------------------------------
     1. HERO
     ------------------------------------------------------------------------------------------------------ */
  const hydrate = (media) => {
    if (!media || !media.hasAttribute('data-evh-lazy')) return;
    media.removeAttribute('data-evh-lazy');
    media.querySelectorAll('source[data-srcset]').forEach((el) => { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); });
    media.querySelectorAll('img').forEach((el) => {
      if (el.dataset.srcset) { el.srcset = el.dataset.srcset; el.removeAttribute('data-srcset'); }
      if (el.dataset.src) { el.src = el.dataset.src; el.removeAttribute('data-src'); }
    });
  };
  const imgOf = (m) => (m ? m.querySelector('img') : null);
  const ready = (m) => { const im = imgOf(m); return !im || (!m.hasAttribute('data-evh-lazy') && im.complete && im.naturalWidth > 0); };

  const hero = (root) => {
    if (!once(root, 'hero')) return;
    const stage = root.querySelector('[data-evh-stage]') || root;
    const slides = Array.from(root.querySelectorAll('[data-evh-slide]'));
    const medias = slides.map((s) => s.querySelector('.evh-hero__media'));
    const dots = Array.from(root.querySelectorAll('[data-evh-dot]'));
    const n = slides.length;
    const ac = new AbortController();
    const on = (el, ev, fn, opt) => el && el.addEventListener(ev, fn, Object.assign({}, opt || {}, { signal: ac.signal }));
    const st = { hover: false, focus: false, touch: false, stopped: false, off: false, hidden: D.hidden, editor: false };
    let i = Math.max(0, slides.findIndex((s) => s.classList.contains('is-active')));
    let timer = 0, due = 0, left = 0, cleanT = 0, waitT = 0;
    const durMs = Math.max(3000, (parseFloat(root.dataset.dur) || 6.5) * 1000);
    left = durMs;
    const auto = () => root.dataset.autoplay === 'true' && n > 1 && !reduce();
    const held = () => st.hover || st.focus || st.touch || st.stopped || st.off || st.hidden || st.editor;
    const stopClock = () => { if (timer) { clearTimeout(timer); timer = 0; left = Math.max(500, due - performance.now()); } };
    const runClock = () => { if (timer || !auto() || held()) return; due = performance.now() + left; timer = setTimeout(advance, left); };
    const resetClock = () => { clearTimeout(timer); timer = 0; left = durMs; };
    const sync = () => {
      root.classList.toggle('is-held', st.hover || st.focus || st.touch || st.editor);
      if (auto() && !held()) runClock(); else stopClock();
    };

    const go = (to) => {
      to = (to + n) % n;
      if (to === i || n < 2) return;
      hydrate(medias[to]);
      hydrate(medias[(to + 1) % n]);
      const prev = slides[i], next = slides[to];
      clearTimeout(cleanT);
      slides.forEach((s) => { if (s !== prev && s !== next) s.classList.remove('is-leaving', 'is-entering'); });
      prev.classList.remove('is-active', 'is-entering');
      prev.setAttribute('inert', '');
      next.classList.remove('is-leaving');
      next.classList.add('is-active');
      next.removeAttribute('inert');
      if (!reduce()) {
        prev.classList.add('is-leaving');
        next.classList.add('is-entering');
        cleanT = setTimeout(() => { prev.classList.remove('is-leaving'); next.classList.remove('is-entering'); }, 1500);
      }
      dots.forEach((d, k) => { if (k === to) d.setAttribute('aria-current', 'true'); else d.removeAttribute('aria-current'); });
      i = to;
      resetClock();
      sync();
    };

    // a photo painted once as a 1 px strip behind the slide on show, before it is first shown (decoded first)
    const painted = new WeakSet();
    const prepaint = (k, then) => {
      const s = slides[k], im = imgOf(medias[k]);
      if (!s || !im || painted.has(s) || k === i || reduce()) { then(); return; }
      if (st.off || st.hidden) { then(); return; }          // off screen a strip paints nothing: try again on screen
      painted.add(s);
      const strip = () => {
        s.classList.add('is-prepaint');
        requestAnimationFrame(() => requestAnimationFrame(() => { s.classList.remove('is-prepaint'); then(); }));
      };
      (im.decode ? im.decode() : Promise.resolve()).then(strip, strip);
    };

    function advance() {
      timer = 0;
      const nx = (i + 1) % n, from = i;
      hydrate(medias[nx]);
      const step = () => {
        if (i !== from || !auto()) return;
        if (held()) { runClock(); return; }
        go(nx);
      };
      const move = () => { clearTimeout(waitT); prepaint(nx, step); };
      if (ready(medias[nx])) { move(); return; }
      const im = imgOf(medias[nx]);
      if (im) { on(im, 'load', move, { once: true }); on(im, 'error', move, { once: true }); }
      waitT = setTimeout(move, 5000);
    }

    // photos 2+: the next as soon as photo 1 has arrived, the rest once the page has loaded
    const warmNext = () => hydrate(medias[(i + 1) % n]);
    const first = imgOf(medias[i]);
    if (!first || (first.complete && first.naturalWidth > 0)) warmNext(); else { on(first, 'load', warmNext, { once: true }); on(first, 'error', warmNext, { once: true }); }
    const warmAll = () => idle(() => medias.forEach(hydrate));
    if (D.readyState === 'complete') warmAll(); else on(W, 'load', warmAll, { once: true });

    dots.forEach((d, k) => on(d, 'click', () => { st.stopped = true; go(k); }));
    on(stage, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { st.hover = true; sync(); } });
    on(stage, 'pointerleave', (e) => { if (e.pointerType === 'mouse') { st.hover = false; sync(); } });
    on(root, 'focusin', (e) => {
      let kb = true;
      try { kb = e.target.matches(':focus-visible'); } catch (err) { /* old browser */ }
      if (kb) { st.focus = true; sync(); }
    });
    on(root, 'focusout', (e) => { if (!root.contains(e.relatedTarget)) { st.focus = false; sync(); } });
    let x0 = null, y0 = 0;
    on(stage, 'touchstart', (e) => { const t = e.touches[0]; x0 = t.clientX; y0 = t.clientY; st.touch = true; sync(); }, { passive: true });
    const lift = (e) => {
      st.touch = false;
      if (x0 !== null && e.changedTouches && e.changedTouches[0]) {
        const t = e.changedTouches[0], dx = t.clientX - x0, dy = t.clientY - y0;
        if (Math.abs(dx) > 44 && Math.abs(dx) > Math.abs(dy) * 1.3) { st.stopped = true; go(i + (dx < 0 ? 1 : -1)); }
      }
      x0 = null;
      sync();
    };
    on(stage, 'touchend', lift, { passive: true });
    on(stage, 'touchcancel', lift, { passive: true });
    let io = null;
    if (hasIO) { io = new IntersectionObserver((es) => { st.off = !es[0].isIntersecting; sync(); }, { threshold: 0.15 }); io.observe(stage); }
    on(D, 'visibilitychange', () => { st.hidden = D.hidden; sync(); });
    on(D, 'shopify:block:select', (e) => { const s = e.target.closest && e.target.closest('[data-evh-slide]'); if (s && root.contains(s)) { st.editor = true; go(slides.indexOf(s)); sync(); } });
    on(D, 'shopify:block:deselect', (e) => { if (root.contains(e.target)) { st.editor = false; sync(); } });
    if (mq.addEventListener) on(mq, 'change', sync);
    sync();
    root.classList.add('is-ready');
    hold(root, () => { ac.abort(); if (io) io.disconnect(); clearTimeout(timer); clearTimeout(cleanT); clearTimeout(waitT); });
  };

  /* ------------------------------------------------------------------------------------------------------
     2. PICK YOUR BOTTLE
     ------------------------------------------------------------------------------------------------------ */
  let visits = -1;
  const visitCount = () => {
    if (visits > -1) return visits;
    visits = 0;
    try {
      const k = 'evh-show-visits';
      visits = parseInt(localStorage.getItem(k) || '0', 10) || 0;
      localStorage.setItem(k, String(visits + 1));
    } catch (e) { visits = 0; }
    return visits;
  };
  const show = (root) => {
    if (!once(root, 'show')) return;
    const rotate = root.hasAttribute('data-evh-rotate');
    const n0 = rotate ? visitCount() : 0;
    root.querySelectorAll('[data-evh-pick]').forEach((card) => {
      const sw = Array.from(card.querySelectorAll('[data-evh-v]'));
      if (!sw.length) return;
      const media = card.querySelector('.evh-pick__media');
      const nameEl = card.querySelector('[data-evh-name]');
      const nowEl = card.querySelector('[data-evh-now]');
      const priceEl = card.querySelector('[data-evh-price]');
      const wasEl = card.querySelector('[data-evh-was]');
      const wasV = card.querySelector('[data-evh-was-v]');
      const saveEl = card.querySelector('[data-evh-save]');
      const pctEl = card.querySelector('[data-evh-pct]');
      const pctB = pctEl ? pctEl.querySelector('b') : null;
      const leftEl = card.querySelector('[data-evh-left]');
      const idEl = card.querySelector('[data-evh-id]');
      const addBtn = card.querySelector('[data-evh-add]');
      const hrefs = Array.from(card.querySelectorAll('[data-evh-href]'));
      const lowW = card.getAttribute('data-low') || 'Only [n] left in [variant]';
      const saveW = card.getAttribute('data-save') || 'Save';
      let chosen = sw.find((b) => b.getAttribute('aria-pressed') === 'true') || sw[0];
      let showing = chosen;
      let current = media ? media.querySelector('.evh-pick__img.is-on') : null;
      const cache = new Map();                              // swatch -> its <img> (made once, kept)
      if (current) cache.set(chosen, current);

      const imgFor = (b) => {
        let im = cache.get(b);
        if (im) return im;
        im = D.createElement('img');
        im.className = 'evh-pick__img';
        im.alt = '';
        im.decoding = 'async';
        im.sizes = current ? current.sizes : '(min-width: 1100px) 25vw, 82vw';
        im.width = 1000; im.height = 1000;
        im.srcset = b.dataset.srcset;
        im.src = b.dataset.src;
        cache.set(b, im);
        if (media) media.insertBefore(im, pctEl || null);
        return im;
      };
      const paint = (b, animate) => {
        const d = b.dataset;
        showing = b;
        if (nameEl) nameEl.textContent = d.name;
        if (nowEl) nowEl.textContent = d.now;
        const sale = (parseInt(d.pct, 10) || 0) > 0;
        if (priceEl) priceEl.classList.toggle('is-sale', sale);
        if (wasEl) wasEl.hidden = !sale;
        if (wasV) wasV.textContent = d.was || '';
        if (saveEl) { saveEl.hidden = !sale; saveEl.textContent = sale ? saveW + ' ' + d.saving : ''; }
        if (pctEl) { pctEl.hidden = !sale; if (pctB) pctB.textContent = d.pct; }
        if (leftEl) { const nLeft = parseInt(d.left, 10) || 0; leftEl.textContent = nLeft > 0 ? lowW.replace('[n]', nLeft).replace('[variant]', d.name) : ''; }
        // the photo: cross-fade to the colour's own packshot once it is decoded
        const im = imgFor(b);
        if (im === current) return;
        const swap = () => {
          if (showing !== b) return;                        // the pointer moved on meanwhile
          if (current) current.classList.remove('is-on', 'is-in');
          im.classList.add('is-on');
          if (animate && !reduce()) { im.classList.remove('is-in'); void im.offsetWidth; im.classList.add('is-in'); }
          current = im;
        };
        if (im.complete && im.naturalWidth > 0) swap();
        else (im.decode ? im.decode() : Promise.resolve()).then(swap, swap);
      };
      const choose = (b, animate) => {
        chosen = b;
        sw.forEach((x) => x.setAttribute('aria-pressed', x === b ? 'true' : 'false'));
        const d = b.dataset;
        if (idEl) idEl.value = d.id;
        hrefs.forEach((a) => { a.href = d.url; });
        if (addBtn) addBtn.setAttribute('aria-label', (addBtn.getAttribute('data-label') || '') + ', ' + d.name);
        paint(b, animate);
      };
      sw.forEach((b) => {
        b.addEventListener('click', () => choose(b, true));
        b.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') paint(b, true); });
        b.addEventListener('focus', () => { imgFor(b); });
      });
      const row = card.querySelector('.evh-pick__dots');
      if (row) row.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse' && showing !== chosen) paint(chosen, true); });
      // warm the other colours' photos while the browser is idle, once the card is near the screen
      if (hasIO) {
        const io = new IntersectionObserver((es) => {
          if (!es[0].isIntersecting) return;
          io.disconnect();
          idle(() => sw.forEach((b) => { if (!cache.has(b)) { const im = imgFor(b); im.loading = 'lazy'; } }));
        }, { rootMargin: '200px' });
        io.observe(card);
        hold(card, () => io.disconnect());
      }
      // colour of the day, moved on for a returning visitor (the first visit keeps the page's own colour)
      if (rotate && n0 > 0 && sw.length > 1) {
        const k = (sw.indexOf(chosen) + n0) % sw.length;
        if (sw[k] !== chosen) choose(sw[k], false);
      }
    });
  };

  /* ------------------------------------------------------------------------------------------------------
     3. ENDLESS GLIDE (moving strip, reviews)
     ------------------------------------------------------------------------------------------------------ */
  const glide = (root) => {
    if (!once(root, 'glide')) return;
    const track = root.querySelector('.evh-mq__track');
    const group = track ? track.querySelector('.evh-mq__group') : null;
    if (!track || !group) return;
    if (reduce()) { root.classList.add('is-static'); return; }
    const speed = parseFloat(root.dataset.speed) || 50;
    let anim = null, rate = 1, target = 1, raf = 0, last = 0, resumeT = 0, rz = 0, width = -1;
    const st = { hover: false, focus: false, touch: false };
    const measure = () => {
      const w = root.clientWidth;
      if (Math.abs(w - width) < 2) return;
      width = w;
      const g = group.getBoundingClientRect().width;
      if (!(g > 0)) return;
      // enough copies that the strip never shows its end: the group + copies >= the screen + one group
      track.querySelectorAll('[data-evh-clone]').forEach((c) => c.remove());
      let have = track.children.length * g;
      while (have < w + g + 2) {
        const c = group.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        c.setAttribute('data-evh-clone', '');
        c.querySelectorAll('a, button, [tabindex]').forEach((f) => f.setAttribute('tabindex', '-1'));
        track.appendChild(c);
        have += g;
      }
      root.style.setProperty('--evh-mq-shift', (-g).toFixed(2) + 'px');
      root.style.setProperty('--evh-mq-dur', (g / speed).toFixed(2) + 's');
      anim = track.getAnimations ? track.getAnimations()[0] || null : null;
    };
    const step = (t) => {
      raf = 0;
      const dt = Math.min(0.05, (t - last) / 1000);
      last = t;
      rate += (target - rate) * (1 - Math.exp(-dt * (target > rate ? 3 : 7)));
      if (Math.abs(target - rate) < 0.01) rate = target;
      if (anim) { try { anim.playbackRate = rate; } catch (e) { /* not supported */ } }
      if (rate !== target) raf = requestAnimationFrame(step);
    };
    const aim = () => {
      target = st.hover || st.focus || st.touch ? 0 : 1;
      if (!anim) anim = track.getAnimations ? track.getAnimations()[0] || null : null;
      if (!raf && rate !== target) { last = performance.now(); raf = requestAnimationFrame(step); }
    };
    const ac = new AbortController();
    const opt = { signal: ac.signal };
    root.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { st.hover = true; aim(); } }, opt);
    root.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { st.hover = false; aim(); } }, opt);
    root.addEventListener('focusin', () => { st.focus = true; aim(); }, opt);
    root.addEventListener('focusout', (e) => { if (!root.contains(e.relatedTarget)) { st.focus = false; aim(); } }, opt);
    root.addEventListener('touchstart', () => { clearTimeout(resumeT); st.touch = true; aim(); }, Object.assign({ passive: true }, opt));
    const up = () => { clearTimeout(resumeT); resumeT = setTimeout(() => { st.touch = false; aim(); }, 2500); };
    root.addEventListener('touchend', up, Object.assign({ passive: true }, opt));
    root.addEventListener('touchcancel', up, Object.assign({ passive: true }, opt));
    W.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(measure, 180); }, opt);
    let io = null;
    if (hasIO) { io = new IntersectionObserver((es) => root.classList.toggle('is-off', !es[0].isIntersecting), { rootMargin: '80px' }); io.observe(root); }
    const start = () => { measure(); root.classList.add('is-ready'); };
    if (D.fonts && D.fonts.ready) D.fonts.ready.then(start); else start();
    hold(root, () => { ac.abort(); if (io) io.disconnect(); if (raf) cancelAnimationFrame(raf); clearTimeout(resumeT); clearTimeout(rz); track.querySelectorAll('[data-evh-clone]').forEach((c) => c.remove()); });
  };

  /* ------------------------------------------------------------------------------------------------------
     4. COUNTDOWN DIGITS that slide in as they change (the sale engine writes them)
     ------------------------------------------------------------------------------------------------------ */
  const flip = (root) => {
    if (!once(root, 'flip') || reduce() || !('MutationObserver' in W)) return;
    const mo = new MutationObserver((list) => {
      list.forEach((r) => {
        const b = r.target.nodeType === 3 ? r.target.parentElement : r.target;
        if (!b || b.tagName !== 'B') return;
        b.classList.remove('is-tick');
        void b.offsetWidth;
        b.classList.add('is-tick');
      });
    });
    root.querySelectorAll('b').forEach((b) => mo.observe(b, { childList: true, characterData: true, subtree: true }));
    hold(root, () => mo.disconnect());
  };

  /* ------------------------------------------------------------------------------------------------------ */
  const init = (scope) => {
    const r = scope && scope.querySelectorAll ? scope : D;
    qsa(r, '[data-evh-hero]').forEach(hero);
    qsa(r, '[data-evh-show]').forEach(show);
    qsa(r, '[data-evh-mq]').forEach(glide);
    qsa(r, '[data-evh-flip]').forEach(flip);
  };
  const release = (scope) => {
    offs.forEach((o) => {
      if (scope !== D && !scope.contains(o.el)) return;
      try { o.off(); } catch (e) { /* gone */ }
      bound.delete(o.el);
      offs.delete(o);
    });
  };
  W.EVHome = { init, release };
  init(D);
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init(D), { once: true });
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => release(e.target));
})();
